'use client'

import { motion } from "framer-motion"
import Image from "next/image"
import { useState } from "react"
import { MapPin, ArrowUpRight } from "lucide-react"
import { RAYONS } from "@/lib/site-config"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { ArticlesGrid } from "./articles-grid"
import { OrnamentalDivider } from "./ornamental-divider"
import { getArticles } from "@/lib/site-config"

function RayonCover({ slug, name, accentColor }: { slug: string; name: string; accentColor: string }) {
  const [error, setError] = useState(false)

  if (error) {
    // Gradient fallback with monogram when image isn't generated yet
    return (
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          background: `linear-gradient(135deg, ${accentColor}, ${accentColor}cc, #1f3a2c)`,
        }}
      >
        <div className="text-center text-white p-6">
          <div className="font-display text-6xl md:text-7xl font-bold opacity-90 mb-2">
            {name.charAt(0)}
          </div>
          <div className="text-xs uppercase tracking-luxe-wide opacity-70">Bientôt disponible</div>
        </div>
      </div>
    )
  }

  return (
    <Image
      src={`/images/rayons/${slug}.png`}
      alt={`Rayon ${name}`}
      fill
      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
      className="object-cover transition-transform duration-700 group-hover:scale-110"
      onError={() => setError(true)}
    />
  )
}

export function RayonsSection() {
  const [active, setActive] = useState<string | null>(null)
  const activeRayon = RAYONS.find(r => r.slug === active)

  return (
    <section id="rayons" className="py-20 md:py-28 relative texture-paper">
      {/* Decorative arabesque background — very subtle */}
      <div className="absolute inset-0 overlay-arabesque opacity-30 pointer-events-none" />

      {/* Top ornamental divider — replaces the brutal transition */}
      <div className="absolute inset-x-0 top-0">
        <div className="h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <Badge variant="outline" className="mb-5 border-gold/40 text-gold tracking-luxe-wide">
            9 univers · 9 destinations
          </Badge>
          <h2 className="font-display text-4xl md:text-6xl font-bold text-cuir mb-5 tracking-luxe">
            Les Rayons du <span className="text-gradient-gold italic">Monde</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/70 max-w-2xl mx-auto leading-[1.8]">
            Chaque rayon est une invitation au voyage. Parcourez les trésors de la Méditerranée,
            les soieries persanes, les bazins brodés d'or et les senteurs de l'Océan Indien.
          </p>
        </motion.div>

        {/* Central ornamental divider before the grid */}
        <div className="max-w-xl mx-auto mb-16">
          <OrnamentalDivider variant="knot" />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {RAYONS.map((r, idx) => (
            <motion.div
              key={r.slug}
              id={`rayon-${r.slug}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: idx * 0.05 }}
            >
              <Card
                onClick={() => setActive(r.slug)}
                className="group relative overflow-hidden cursor-pointer border border-gold/20 hover:border-gold/60 transition-all duration-500 shadow-luxury hover:shadow-luxe-deep bg-card"
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <RayonCover slug={r.slug} name={r.name} accentColor={r.accentColor} />
                  {/* Layered overlays — keep image visible, deepen bottom for legibility */}
                  <div className="absolute inset-0 bg-gradient-to-t from-cuir via-cuir/25 to-transparent opacity-90" />
                  {/* Moucharabieh overlay — subtle oriental texture on every cover */}
                  <div className="absolute inset-0 overlay-moucharabieh opacity-25 mix-blend-overlay" />
                  {/* Region badge — refined */}
                  <div className="absolute top-3 right-3">
                    <Badge
                      variant="secondary"
                      className="bg-primary-foreground/95 backdrop-blur text-cuir text-[10px] uppercase tracking-luxe-wide border border-gold/40 shadow-luxury"
                    >
                      <MapPin className="h-3 w-3 mr-1" />
                      {r.region}
                    </Badge>
                  </div>
                  {/* Title overlay — refined */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 text-primary-foreground">
                    <div className="flex items-end justify-between gap-2">
                      <div>
                        <h3 className="font-display text-2xl font-semibold leading-tight tracking-luxe">
                          {r.name}
                        </h3>
                        <p className="text-sm text-gold italic tracking-luxe">{r.subtitle}</p>
                      </div>
                      <ArrowUpRight className="h-6 w-6 text-gold opacity-70 group-hover:opacity-100 group-hover:rotate-45 transition-all" />
                    </div>
                  </div>
                </div>
                {/* Body */}
                <div className="p-5">
                  <p className="text-sm text-foreground/80 leading-[1.7] line-clamp-3">
                    {r.description}
                  </p>
                  {/* Ornamental gold filet — replaces flat colored line */}
                  <div className="mt-4 flex items-center gap-2">
                    <span
                      className="h-px flex-1"
                      style={{ background: `linear-gradient(90deg, ${r.accentColor}, transparent)` }}
                    />
                    <svg viewBox="0 0 16 16" className="h-3 w-3 flex-shrink-0" fill="none" stroke={r.accentColor} strokeWidth="1.2">
                      <path d="M8 1 L15 8 L8 15 L1 8 Z" />
                      <circle cx="8" cy="8" r="1.5" fill={r.accentColor} />
                    </svg>
                    <span
                      className="h-px flex-1"
                      style={{ background: `linear-gradient(90deg, transparent, ${r.accentColor})` }}
                    />
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bottom ornamental divider — closes the section */}
        <div className="max-w-xl mx-auto mt-16">
          <OrnamentalDivider variant="default" />
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto custom-scrollbar bg-background">
          {activeRayon && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: activeRayon.accentColor }}
                  />
                  <Badge variant="outline" className="border-gold/40 text-gold tracking-luxe-wide">
                    {activeRayon.region}
                  </Badge>
                  <Badge variant="secondary" className="bg-cuir/10 text-cuir border border-gold/30">
                    {getArticles(activeRayon.slug).length} articles en boutique
                  </Badge>
                </div>
                <DialogTitle className="font-display text-4xl text-cuir tracking-luxe">
                  {activeRayon.name}
                </DialogTitle>
                <DialogDescription className="font-display italic text-emerald text-lg tracking-luxe">
                  {activeRayon.subtitle}
                </DialogDescription>
              </DialogHeader>
              <p className="text-foreground/80 leading-[1.7] mt-2">
                {activeRayon.description}
              </p>
              <ArticlesGrid rayonSlug={activeRayon.slug} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}
